import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Waves, Plus, AlertTriangle, CheckCircle2, Clock, Info, Loader2, FileWarning, Download, X, Calendar as CalIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { companiesApi } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('safetydesk81_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const rawToken = () => localStorage.getItem('safetydesk81_token') || '';

const TEMP_OPTS = ['18-20', '20-22', '22-24', '24-26', '26-28', '28-30'];
const PH_OPTS = ['6.5-6.7', '6.7-6.9', '6.9-7.1', '7.1-7.3', '7.3-7.5'];
const CLORO_LIB_OPTS = ['0.7-0.9', '0.9-1.1', '1.1-1.3', '1.3-1.5'];
const CLORO_COMB_OPTS = ['0-0.1', '0.1-0.2', '0.2-0.3', '0.3-0.4'];
const VISUAL_MAP = {
  torbidita: [['limpida', 'Limpida', 'g'], ['leggermente_torbida', 'Leggermente torbida', 'y'], ['torbida', 'Torbida', 'r']],
  colore: [['normale', 'Normale', 'g'], ['alterato', 'Alterato', 'y'], ['anomalo', 'Anomalo', 'r']],
  solidi: [['assenti', 'Assenti', 'g'], ['presenti', 'Presenti', 'r']],
};
const CAUSALI = ['Evaporazione', 'Controlavaggio', 'Rinnovo', 'Altro'];
const cls = {
  g: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  y: 'bg-amber-100 text-amber-800 border-amber-300',
  r: 'bg-red-100 text-red-800 border-red-400',
  n: 'bg-slate-100 text-slate-600 border-slate-300',
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Lunedì della settimana ISO (locale, cross-mese/anno)
const mondayOf = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDay() || 7; // domenica=7
  d.setDate(d.getDate() - (day - 1));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Select = ({ value, onChange, options, placeholder = 'Seleziona…', ncOption = true, testId }) => {
  const isNC = value === 'non_conforme';
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      data-testid={testId}
      className={`w-full px-2 py-1.5 rounded border text-sm ${isNC ? 'bg-red-100 border-red-400 text-red-800 font-bold' : 'border-slate-300 bg-white'}`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
      {ncOption && <option value="non_conforme">Non conforme</option>}
    </select>
  );
};

const VisualSelect = ({ value, onChange, field, testId }) => {
  const opts = VISUAL_MAP[field];
  const active = opts.find((o) => o[0] === value);
  const badgeCls = active ? cls[active[2]] : cls.n;
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} className={`w-full px-2 py-1.5 rounded border text-sm font-semibold ${badgeCls}`}>
      <option value="">Seleziona…</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
};

const EsitoBadge = ({ esito }) => {
  const map = {
    conforme: ['Conforme ai parametri registrati', cls.g, CheckCircle2],
    attenzione: ['Attenzione', cls.y, AlertTriangle],
    non_conforme: ['Non conforme', cls.r, AlertTriangle],
    incompleto: ['Incompleto', cls.n, Clock],
    da_verificare: ['Da verificare', cls.n, Clock],
    favorevole: ['Favorevole (parametri registrati)', cls.g, CheckCircle2],
    non_applicabile: ['Verifica chimica non applicabile', cls.n, Info],
  };
  const [label, cx, Icon] = map[esito] || ['—', cls.n, Info];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cx}`}><Icon size={11} />{label}</span>;
};

// Popup NC unificato riutilizzabile
const NCAlertPopup = ({ open, params, level, onCreate, onClose }) => {
  if (!open) return null;
  const isRed = level === 'red';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nc-popup">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 border-t-4 ${isRed ? 'border-red-600' : 'border-amber-500'}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle size={26} className={isRed ? 'text-red-600 shrink-0' : 'text-amber-600 shrink-0'} />
          <div>
            <h3 className={`text-lg font-bold ${isRed ? 'text-red-800' : 'text-amber-800'}`}>
              {isRed ? 'Attenzione: parametro non conforme' : 'Attenzione: valore critico'}
            </h3>
            <p className="text-sm text-slate-700 mt-1">
              {isRed
                ? `È stato registrato un parametro non conforme (${params.join(', ')}). Documenta l'anomalia e le azioni intraprese nel registro delle non conformità (P.0.6.5).`
                : `Rilevato un valore "attenzione" (${params.join(', ')}). Non è una non conformità, ma verifica la situazione. Non è richiesta un'azione automatica.`}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Chiudi</button>
          {isRed && (
            <button onClick={onCreate} data-testid="nc-popup-create" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold">
              Registra/Gestisci non conformità
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Multi-select prodotti con "+ Nuovo"
const ProductsPicker = ({ companyId, value = [], onChange, testId }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/piscine/products/list`, { params: { companyId }, headers: authHeaders() });
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const name = (q || '').trim();
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/piscine/products/create`, { companyId, name }, { headers: authHeaders() });
      setQ('');
      await load();
      if (!value.includes(data.name)) onChange([...(value || []), data.name]);
      toast.success('Prodotto aggiunto');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const toggle = (name) => onChange(value.includes(name) ? value.filter((x) => x !== name) : [...value, name]);
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex flex-wrap gap-1">
        {(value || []).map((n) => (
          <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-800 text-[11px] font-semibold">
            {n} <button onClick={() => toggle(n)} className="hover:text-red-700">×</button>
          </span>
        ))}
        {(value || []).length === 0 && <span className="text-[11px] text-slate-400">nessun prodotto</span>}
      </div>
      <div className="flex gap-1">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca o aggiungi…" className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded" />
        <button onClick={create} disabled={!q.trim()} className="px-2 py-1 text-xs bg-emerald-600 text-white rounded font-bold disabled:opacity-60"><Plus size={11} /></button>
      </div>
      {items.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6).map((p) => (
        <button key={p.id} onClick={() => toggle(p.name)} className={`block w-full text-left px-2 py-1 text-[11px] rounded hover:bg-slate-100 ${value.includes(p.name) ? 'bg-slate-100 font-bold' : ''}`}>
          {value.includes(p.name) ? '✓ ' : ''}{p.name}
        </button>
      ))}
    </div>
  );
};

// Download PDF
const exportPDF = async (pid, registro, dateFrom, dateTo) => {
  try {
    const url = `${API}/piscine/${pid}/export/pdf?registro=${registro}&dateFrom=${dateFrom}${dateTo ? `&dateTo=${dateTo}` : ''}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${rawToken()}` } });
    if (!res.ok) throw new Error(await res.text());
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `piscina_${registro}_${dateFrom}${dateTo ? `_${dateTo}` : ''}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('PDF generato');
  } catch (e) { toast.error('Errore export PDF'); }
};

// -------------------- PAGE ROOT --------------------
const PiscinaPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [piscine, setPiscine] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedPid, setSelectedPid] = useState(null);
  const [tab, setTab] = useState('riepilogo');
  const [loading, setLoading] = useState(true);
  const [newPoolOpen, setNewPoolOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reloadPools = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/piscine`, { headers: authHeaders() });
      setPiscine(data || []);
      if (!selectedPid && data && data.length > 0) setSelectedPid(data[0].id);
    } catch (e) {
      if (e.response?.status === 403) toast.error(e.response.data?.detail || 'Modulo non abilitato');
    } finally { setLoading(false); }
  };
  useEffect(() => {
    reloadPools();
    // Carico il nome dell'azienda sia per consulenti (per il picker della
    // creazione) sia per i clienti (serve al breadcrumb "Azienda · Vasca").
    companiesApi.list({ status: 'all' })
      .then((r) => setCompanies(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPool = useMemo(() => piscine.find((p) => p.id === selectedPid), [piscine, selectedPid]);

  return (
    <div className="space-y-4" data-testid="piscina-page">
      <div className="rounded-2xl bg-gradient-to-r from-[#0284c7] to-[#023F80] text-white p-5 flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Waves size={26} className="text-[#FFCB05]" /></div>
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-extrabold">Piscina — Registri operativi</h1>
          <p className="text-white/70 text-sm mt-1">Compilazione digitale P.0.6.1 / P.0.6.2 / P.0.6.4 / P.0.6.5 con calendario, prodotti, export PDF.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {piscine.length > 1 && (
            <select value={selectedPid || ''} onChange={(e) => setSelectedPid(e.target.value)} data-testid="piscina-selector" className="px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-white text-sm font-semibold">
              {piscine.map((p) => <option key={p.id} value={p.id} className="text-slate-800">{p.name}</option>)}
            </select>
          )}
          {!isClient && selectedPool && (
            <button onClick={() => setDeleteTarget(selectedPool)} data-testid="piscina-delete-btn" className="ro-hide inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700" title={`Elimina vasca "${selectedPool.name}"`}>
              <X size={14} /> Elimina vasca
            </button>
          )}
          {!isClient && <button onClick={() => setNewPoolOpen(true)} data-testid="piscina-new-btn" className="ro-hide inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FFCB05] text-[#023F80] text-xs font-bold hover:bg-yellow-400"><Plus size={14} /> Nuova piscina</button>}
        </div>
      </div>

      {/* Breadcrumb azienda / vasca selezionata */}
      {!loading && selectedPool && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-2" data-testid="piscina-breadcrumb">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Azienda</span>
          <span className="font-semibold text-slate-800">{companies.find((c) => c.id === selectedPool.companyId)?.name || selectedPool.companyId}</span>
          <span className="text-slate-400">·</span>
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Vasca</span>
          <span className="font-semibold text-[#023F80]">{selectedPool.name}</span>
          {isClient && piscine.length > 1 && (
            <>
              <span className="text-slate-400">·</span>
              <select value={selectedPid || ''} onChange={(e) => setSelectedPid(e.target.value)} data-testid="piscina-client-selector" className="px-2 py-0.5 rounded border border-slate-300 text-xs">
                {piscine.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      {loading && <div className="text-center py-10 text-slate-500"><Loader2 className="inline animate-spin mr-2" size={18} />Caricamento…</div>}

      {!loading && piscine.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <FileWarning size={28} className="mx-auto text-amber-700 mb-2" />
          <div className="text-sm font-bold text-amber-900">Nessuna piscina configurata o modulo non abilitato</div>
          <div className="text-xs text-amber-800 mt-1">{isClient ? 'Chiedi al tuo consulente di abilitare il modulo Piscina e configurare la vasca.' : 'Clicca "Nuova piscina" per configurare la prima piscina di un\'azienda cliente.'}</div>
        </div>
      )}

      {!loading && selectedPool && (
        <>
          <div className="flex flex-wrap gap-2">
            {[
              ['riepilogo', 'Riepilogo'],
              ['acqua', 'Controllo Acqua'],
              ['pulizie', 'Pulizie'],
              ['reintegri', 'Reintegri'],
              ['nc', 'Non Conformità'],
              ['calendario', 'Calendario'],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`piscina-tab-${k}`} className={`px-4 py-2 rounded-full text-xs font-bold ${tab === k ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{l}</button>
            ))}
          </div>
          {tab === 'riepilogo' && <RiepilogoTab pool={selectedPool} onNav={setTab} />}
          {tab === 'acqua' && <AcquaTab pool={selectedPool} />}
          {tab === 'pulizie' && <PulizieTab pool={selectedPool} />}
          {tab === 'reintegri' && <ReintegriTab pool={selectedPool} />}
          {tab === 'nc' && <NCTab pool={selectedPool} />}
          {tab === 'calendario' && <CalendarTab pool={selectedPool} onNav={(t, d) => { setTab(t); if (d) window.dispatchEvent(new CustomEvent('piscina-goto-date', { detail: d })); }} />}
        </>
      )}

      {newPoolOpen && <NewPoolDialog companies={companies} onClose={() => setNewPoolOpen(false)} onCreated={() => { setNewPoolOpen(false); reloadPools(); }} />}
      {deleteTarget && (
        <DeletePoolDialog
          pool={deleteTarget}
          companyName={companies.find((c) => c.id === deleteTarget.companyId)?.name || ''}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(nextList) => {
            setDeleteTarget(null);
            // Dopo il delete ricarica e sposta la selezione sulla prossima vasca
            setSelectedPid(null);
            reloadPools();
          }}
        />
      )}
    </div>
  );
};

// -------------------- Riepilogo --------------------
const RiepilogoTab = ({ pool, onNav }) => {
  const [date, setDate] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [statoDialog, setStatoDialog] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/piscine/${pool.id}/summary`, { params: { date }, headers: authHeaders() });
    setSummary(data);
  };
  useEffect(() => { load(); }, [pool.id, date]); // eslint-disable-line
  const statoBadge = (s) => {
    const map = { aperta: ['Aperta', cls.g], chiusa: ['Chiusa al pubblico', cls.r], non_dichiarato: ['Non dichiarato', cls.n] };
    const [l, c] = map[s] || ['—', cls.n];
    return <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border ${c}`}>{l}</span>;
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="piscina-riepilogo">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold text-slate-600">Data</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="riepilogo-date" className="px-3 py-1.5 rounded border border-slate-300 text-sm" />
        <button onClick={() => setStatoDialog(true)} data-testid="riepilogo-declare-btn" className="ml-auto px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]">Dichiara stato (aperta/chiusa)</button>
        <button onClick={() => { const first = date.slice(0, 7) + '-01'; const y = parseInt(date.slice(0, 4), 10); const m = parseInt(date.slice(5, 7), 10); const last = new Date(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 0); const lastIso = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`; exportPDF(pool.id, 'all', first, lastIso); }} data-testid="riepilogo-pdf-all" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 inline-flex items-center gap-1"><Download size={12} /> Esporta tutti i registri (mese)</button>
      </div>
      {summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card label="Stato giornata">{statoBadge(summary.stato)}{summary.statoMotivo && <div className="text-[11px] text-slate-500 mt-1 italic">{summary.statoMotivo}</div>}</Card>
            <Card label="Controllo acqua"><EsitoBadge esito={summary.acquaEsiti?.overall} /></Card>
            <Card label="Reintegri oggi"><div className="text-2xl font-extrabold text-[#023F80]">{summary.reintegriOggi}</div></Card>
            <Card label="NC aperte"><div className={`text-2xl font-extrabold ${summary.ncAperte > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{summary.ncAperte}</div></Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card label="Pulizie giornaliere">{summary.pulizieOggiCompleto ? <EsitoBadge esito="conforme" /> : summary.stato === 'chiusa' ? <span className="text-xs italic text-slate-500">Non richieste (piscina chiusa)</span> : <span className="text-xs text-amber-700 font-semibold">Da compilare</span>}</Card>
            <Card label={`Pulizie settimanali (sett. dal ${summary.settimanaLun})`}>{summary.pulizieSettimanaCompleto ? <EsitoBadge esito="conforme" /> : <span className="text-xs text-amber-700 font-semibold">Da compilare</span>}</Card>
          </div>
          {summary.stato === 'chiusa' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900">
              📌 Piscina dichiarata <b>chiusa al pubblico</b> per questa giornata. I registri ordinari (acqua, pulizie) <b>non</b> risultano mancanti.
              Puoi comunque registrare manutenzioni, reintegri e non conformità.
            </div>
          )}
          {summary.todo && summary.todo.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="riepilogo-todo">
              <div className="text-xs font-bold text-amber-900 mb-2">Da compilare oggi:</div>
              <div className="flex flex-wrap gap-2">
                {summary.todo.map((t) => (
                  <button key={t.registro} onClick={() => onNav(t.link)} data-testid={`todo-${t.registro}`} className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-amber-900 text-xs font-bold hover:bg-amber-100">
                    → {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {statoDialog && <StatoDialog pool={pool} defaultDate={date} onClose={() => { setStatoDialog(false); load(); }} />}
    </div>
  );
};

const Card = ({ label, children }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

const StatoDialog = ({ pool, defaultDate, onClose }) => {
  const [dateFrom, setDateFrom] = useState(defaultDate);
  const [dateTo, setDateTo] = useState(defaultDate);
  const [stato, setStato] = useState('aperta');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/piscine/${pool.id}/status`, { dateFrom, dateTo, stato, motivo }, { headers: authHeaders() });
      toast.success('Stato dichiarato. Le registrazioni esistenti sono conservate.');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="stato-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#023F80]">Dichiarazione stato piscina</h3>
        <p className="text-xs text-slate-500">Le registrazioni esistenti (acqua, reintegri, NC, pulizie) non vengono mai cancellate.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold text-slate-600">Dal<input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300" data-testid="stato-from" /></label>
          <label className="text-xs font-bold text-slate-600">Al<input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300" data-testid="stato-to" /></label>
        </div>
        <div className="flex gap-2">
          {['aperta', 'chiusa', 'non_dichiarato'].map((s) => (
            <button key={s} onClick={() => setStato(s)} data-testid={`stato-radio-${s}`} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border ${stato === s ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-300'}`}>
              {s === 'aperta' ? 'Aperta' : s === 'chiusa' ? 'Chiusa' : 'Non dichiarato'}
            </button>
          ))}
        </div>
        <label className="block text-xs font-bold text-slate-600">Motivo (facoltativo)
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Es. chiusura stagionale, maltempo, manutenzione..." data-testid="stato-motivo" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="stato-save" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-60">{saving ? 'Salvo…' : 'Salva'}</button>
        </div>
      </div>
    </div>
  );
};

// -------------------- Acqua --------------------
const AcquaTab = ({ pool }) => {
  const [date, setDate] = useState(todayISO());
  useEffect(() => {
    const h = (e) => { if (e.detail) setDate(e.detail); };
    window.addEventListener('piscina-goto-date', h);
    return () => window.removeEventListener('piscina-goto-date', h);
  }, []);
  const [rec, setRec] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ncPrompt, setNcPrompt] = useState(null);
  const [existingNC, setExistingNC] = useState(null);
  const load = async () => {
    const { data } = await axios.get(`${API}/piscine/${pool.id}/acqua`, { params: { date }, headers: authHeaders() });
    setRec(data);
    if (data.id) {
      const nc = await axios.get(`${API}/piscine/${pool.id}/nc`, { headers: authHeaders() });
      setExistingNC((nc.data.items || []).find((x) => x.sourceRegistroId === data.id && x.statoPratica !== 'risolta') || null);
    }
  };
  useEffect(() => { load(); }, [pool.id, date]); // eslint-disable-line

  const detectAlert = (patch) => {
    const red = [], yellow = [];
    Object.entries(patch).forEach(([k, v]) => {
      if (k === 'v1' || k === 'v2') Object.entries(v).forEach(([kk, vv]) => { if (vv === 'non_conforme') red.push(`${k}.${kk}`); });
      else if (['torbida', 'anomalo', 'presenti'].includes(v)) red.push(k);
      else if (['leggermente_torbida', 'alterato'].includes(v)) yellow.push(k);
    });
    if (red.length) setNcPrompt({ params: red, level: 'red' });
    else if (yellow.length) setNcPrompt({ params: yellow, level: 'yellow' });
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/piscine/${pool.id}/acqua/${date}`, patch, { headers: authHeaders() });
      setRec(data);
      toast.success('Salvato');
      detectAlert(patch);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const openOrCreateNC = async () => {
    if (existingNC) { toast.info(`NC ${existingNC.statoPratica} già esistente`); setNcPrompt(null); return; }
    try {
      const { data } = await axios.post(`${API}/piscine/${pool.id}/nc`, {
        registroCollegato: 'acqua', sourceRegistroId: rec.id,
        descrizione: `Parametro non conforme controllo acqua del ${date} (${(ncPrompt?.params || []).join(', ')})`,
      }, { headers: authHeaders() });
      toast.success('NC creata: apri il tab Non conformità per compilare');
      setExistingNC(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setNcPrompt(null); }
  };

  if (!rec) return <Loader2 className="animate-spin" />;
  const esiti = rec.esiti || {};

  const CheckSlot = ({ slot, title }) => {
    const v = rec[slot] || {};
    const [tempV, setTempV] = useState(v.tempVasca || null);
    const [ph, setPh] = useState(v.pH || null);
    const [cl, setCl] = useState(v.cloroLibero || null);
    const [cc, setCc] = useState(v.cloroCombinato || null);
    useEffect(() => {
      setTempV(v.tempVasca || null); setPh(v.pH || null); setCl(v.cloroLibero || null); setCc(v.cloroCombinato || null);
    }, [rec.date, slot]); // eslint-disable-line
    const submit = () => save({ [slot]: { tempVasca: tempV, pH: ph, cloroLibero: cl, cloroCombinato: cc } });
    return (
      <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`acqua-${slot}`}>
        <div className="flex items-center justify-between"><h4 className="font-bold text-[#023F80]">{title}</h4><EsitoBadge esito={esiti[slot]} /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Temperatura °C</div><Select value={tempV} onChange={setTempV} options={TEMP_OPTS} testId={`${slot}-temp`} /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">pH</div><Select value={ph} onChange={setPh} options={PH_OPTS} testId={`${slot}-ph`} /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro libero mg/L</div><Select value={cl} onChange={setCl} options={CLORO_LIB_OPTS} testId={`${slot}-cl`} /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro combinato mg/L</div><Select value={cc} onChange={setCc} options={CLORO_COMB_OPTS} testId={`${slot}-cc`} /></div>
        </div>
        <button onClick={submit} disabled={saving} data-testid={`${slot}-save`} className="px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold disabled:opacity-60">Salva {title}</button>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="piscina-acqua-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold text-slate-600">Data</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="acqua-date" className="px-3 py-1.5 rounded border border-slate-300 text-sm" />
        <div className="ml-auto flex items-center gap-2"><EsitoBadge esito={esiti.overall} />
          <button onClick={() => exportPDF(pool.id, 'acqua', date, date)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300"><Download size={12} /> PDF giorno</button>
        </div>
      </div>
      <CheckSlot slot="v1" title="1ª verifica — prima dell'apertura" />
      <CheckSlot slot="v2" title="2ª verifica — alla chiusura" />
      <div className="rounded-xl border border-slate-200 p-4">
        <h4 className="font-bold text-[#023F80] mb-3">Aspetto vasca (una sola volta al giorno)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Torbidità</div><VisualSelect value={rec.torbidita} onChange={(v) => save({ torbidita: v })} field="torbidita" testId="acqua-torbidita" /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Colore</div><VisualSelect value={rec.colore} onChange={(v) => save({ colore: v })} field="colore" testId="acqua-colore" /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Solidi grossolani</div><VisualSelect value={rec.solidi} onChange={(v) => save({ solidi: v })} field="solidi" testId="acqua-solidi" /></div>
        </div>
      </div>
      {existingNC && (
        <div className="rounded-xl bg-red-50 border border-red-300 p-3 text-xs text-red-900 flex items-center gap-2">
          <AlertTriangle size={14} /> NC collegata a questa giornata già esistente (stato: <b>{existingNC.statoPratica}</b>). Aprila dal tab Non conformità.
        </div>
      )}
      <NCAlertPopup open={!!ncPrompt} params={ncPrompt?.params || []} level={ncPrompt?.level} onCreate={openOrCreateNC} onClose={() => setNcPrompt(null)} />
    </div>
  );
};

// -------------------- Pulizie --------------------
const PulizieTab = ({ pool }) => {
  const [sub, setSub] = useState('giornaliero');
  return (
    <div className="space-y-3" data-testid="pulizie-tab">
      <div className="flex gap-2">
        <button onClick={() => setSub('giornaliero')} data-testid="pulizie-sub-daily" className={`px-3 py-1.5 rounded-full text-xs font-bold ${sub === 'giornaliero' ? 'bg-[#023F80] text-white' : 'bg-slate-100'}`}>Giornaliero (6 righe)</button>
        <button onClick={() => setSub('settimanale')} data-testid="pulizie-sub-weekly" className={`px-3 py-1.5 rounded-full text-xs font-bold ${sub === 'settimanale' ? 'bg-[#023F80] text-white' : 'bg-slate-100'}`}>Settimanale (lun–dom)</button>
      </div>
      {sub === 'giornaliero' ? <PulizieDaily pool={pool} /> : <PulizieWeekly pool={pool} />}
    </div>
  );
};

const PulizieDaily = ({ pool }) => {
  const [date, setDate] = useState(todayISO());
  const [rec, setRec] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/piscine/${pool.id}/pulizie/giornaliero`, { params: { date }, headers: authHeaders() });
    setRec(data);
  };
  useEffect(() => { load(); }, [pool.id, date]); // eslint-disable-line
  const setRow = (rid, k, v) => setRec((r) => ({ ...r, rows: r.rows.map((x) => x.id === rid ? { ...x, [k]: v } : x) }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/piscine/${pool.id}/pulizie/giornaliero/${date}`, { rows: rec.rows }, { headers: authHeaders() });
      setRec(data); toast.success('Salvato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  if (!rec) return <Loader2 className="animate-spin" />;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="pulizie-daily-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold text-slate-600">Data</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pulizie-daily-date" className="px-3 py-1.5 rounded border border-slate-300 text-sm" />
        <span className={`ml-2 inline-flex px-3 py-1 rounded-full text-xs font-bold border ${rec.completo ? cls.g : cls.n}`}>{rec.completo ? 'Completato' : 'Da completare'}</span>
        <button onClick={() => exportPDF(pool.id, 'pulizie_daily', date, date)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300"><Download size={12} /> PDF</button>
      </div>
      <div className="space-y-3">
        {rec.rows.map((row) => (
          <div key={row.id} className={`rounded-xl border p-4 ${row.eseguito ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`} data-testid={`pul-row-${row.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-[220px]">
                <input type="checkbox" checked={row.eseguito} onChange={(e) => setRow(row.id, 'eseguito', e.target.checked)} data-testid={`pul-check-${row.id}`} className="mt-1 w-5 h-5" />
                <div>
                  <div className="text-[11px] uppercase font-bold text-slate-500">{row.fascia}</div>
                  <div className="font-bold text-slate-800">{row.area}</div>
                  <div className="text-xs text-slate-600 italic">{row.intervento}</div>
                </div>
              </label>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Prodotti usati</div>
                <ProductsPicker companyId={pool.companyId} value={row.prodotti || []} onChange={(v) => setRow(row.id, 'prodotti', v)} testId={`pul-prd-${row.id}`} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Note</div>
                <textarea value={row.note || ''} onChange={(e) => setRow(row.id, 'note', e.target.value)} data-testid={`pul-note-${row.id}`} rows={2} className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="pul-save" className="px-5 py-2 rounded-full bg-[#023F80] text-white text-sm font-bold disabled:opacity-60">{saving ? 'Salvo…' : 'Salva scheda giornaliera'}</button>
      <p className="text-[11px] text-slate-500 italic">Una casella non spuntata significa "attività non registrata come eseguita", NON è una non conformità.</p>
    </div>
  );
};

const PulizieWeekly = ({ pool }) => {
  const [weekDate, setWeekDate] = useState(todayISO());
  const [rec, setRec] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // {altra, index}
  const [deleteReason, setDeleteReason] = useState('');
  const load = async () => {
    const ws = mondayOf(weekDate);
    const { data } = await axios.get(`${API}/piscine/${pool.id}/pulizie/settimanale`, { params: { week: ws }, headers: authHeaders() });
    setRec(data);
  };
  useEffect(() => { load(); }, [pool.id, weekDate]); // eslint-disable-line
  const setRow = (rid, k, v) => setRec((r) => ({ ...r, rows: r.rows.map((x) => x.id === rid ? { ...x, [k]: v } : x) }));
  const setAltra = (aid, k, v) => setRec((r) => ({ ...r, altre: r.altre.map((x) => x.id === aid ? { ...x, [k]: v } : x) }));
  const addAltra = () => setRec((r) => ({ ...r, altre: [...(r.altre || []), { id: `new-${Date.now()}`, area: '', intervento: '', eseguito: false, dataEsecuzione: null, prodotti: [], note: '' }] }));
  const requestRemove = (altra) => {
    if (String(altra.id || '').startsWith('new-')) {
      setRec((r) => ({ ...r, altre: r.altre.filter((x) => x.id !== altra.id) }));
      return;
    }
    setPendingDelete(altra);
    setDeleteReason('');
  };
  const confirmRemove = async () => {
    if (!pendingDelete) return;
    const wasComplete = rec.completo;
    if (wasComplete && !deleteReason.trim()) {
      toast.error('Motivo obbligatorio: la scheda era completa');
      return;
    }
    setSaving(true);
    try {
      const ws = mondayOf(weekDate);
      const newAltre = rec.altre.filter((x) => x.id !== pendingDelete.id);
      const { data } = await axios.put(`${API}/piscine/${pool.id}/pulizie/settimanale/${ws}`, { rows: rec.rows, altre: newAltre, reason: deleteReason.trim() || undefined }, { headers: authHeaders() });
      setRec(data); toast.success('Riga rimossa (tracciata in cronologia)');
      setPendingDelete(null); setDeleteReason('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const ws = mondayOf(weekDate);
      const { data } = await axios.put(`${API}/piscine/${pool.id}/pulizie/settimanale/${ws}`, { rows: rec.rows, altre: rec.altre }, { headers: authHeaders() });
      setRec(data); toast.success('Salvato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  if (!rec) return <Loader2 className="animate-spin" />;
  const wd = rec.weekDays || [];
  const rangeLabel = wd.length === 7 ? `Da lun ${wd[0]} a dom ${wd[6]}` : '';
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="pulizie-weekly-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold text-slate-600">Settimana di</label>
        <input type="date" value={weekDate} onChange={(e) => setWeekDate(e.target.value)} data-testid="pul-week-date" className="px-3 py-1.5 rounded border border-slate-300 text-sm" />
        <span className="text-xs text-slate-500 italic">{rangeLabel}</span>
        <span className={`ml-2 inline-flex px-3 py-1 rounded-full text-xs font-bold border ${rec.completo ? cls.g : cls.n}`}>{rec.completo ? 'Completata' : 'Da completare'}</span>
        <button onClick={() => wd.length > 0 && exportPDF(pool.id, 'pulizie_weekly', wd[0], wd[6])} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300"><Download size={12} /> PDF settimana</button>
      </div>
      <div className="space-y-2">
        {rec.rows.map((row) => (
          <div key={row.id} className={`rounded-xl border p-3 ${row.eseguito ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`}>
            <div className="flex flex-wrap items-start gap-3">
              <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-[220px]">
                <input type="checkbox" checked={row.eseguito} onChange={(e) => setRow(row.id, 'eseguito', e.target.checked)} data-testid={`pulw-check-${row.id}`} className="mt-1 w-5 h-5" />
                <div><div className="font-bold text-slate-800">{row.area}</div><div className="text-xs text-slate-600 italic">{row.intervento}</div></div>
              </label>
              <div className="min-w-[180px]"><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Data esecuzione</div>
                <input type="date" min={wd[0]} max={wd[6]} value={row.dataEsecuzione || ''} onChange={(e) => setRow(row.id, 'dataEsecuzione', e.target.value || null)} data-testid={`pulw-data-${row.id}`} className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
              </div>
              <div className="flex-1 min-w-[220px]"><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Prodotti</div>
                <ProductsPicker companyId={pool.companyId} value={row.prodotti || []} onChange={(v) => setRow(row.id, 'prodotti', v)} />
              </div>
              <div className="flex-1 min-w-[220px]"><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Note</div>
                <input value={row.note || ''} onChange={(e) => setRow(row.id, 'note', e.target.value)} className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-2" data-testid="pulw-altre">
        <div className="flex items-center justify-between"><div className="text-xs font-bold text-slate-700">Altri interventi (facoltativi)</div>
          <button onClick={addAltra} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-[11px] font-bold"><Plus size={11} /> Aggiungi</button>
        </div>
        {(rec.altre || []).map((a) => (
          <div key={a.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-start p-2 rounded bg-slate-50">
            <input placeholder="Area" value={a.area} onChange={(e) => setAltra(a.id, 'area', e.target.value)} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
            <input placeholder="Intervento" value={a.intervento} onChange={(e) => setAltra(a.id, 'intervento', e.target.value)} className="px-2 py-1.5 rounded border border-slate-300 text-sm md:col-span-2" />
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={a.eseguito} onChange={(e) => setAltra(a.id, 'eseguito', e.target.checked)} /> Eseguito</label>
            <input type="date" min={wd[0]} max={wd[6]} value={a.dataEsecuzione || ''} onChange={(e) => setAltra(a.id, 'dataEsecuzione', e.target.value || null)} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
            <button onClick={() => requestRemove(a)} data-testid={`pulw-del-${a.id}`} className="px-2 py-1 text-red-700 text-xs hover:bg-red-50 rounded"><X size={12} /></button>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="pulw-save" className="px-5 py-2 rounded-full bg-[#023F80] text-white text-sm font-bold disabled:opacity-60">{saving ? 'Salvo…' : 'Salva scheda settimanale'}</button>
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="pulw-delete-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPendingDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-bold text-red-800">Rimuovi riga già salvata</h3>
            <div className="text-xs text-slate-700">Riga: <b>{pendingDelete.area}</b> — <i>{pendingDelete.intervento}</i></div>
            <p className="text-xs text-slate-500">La riga sarà rimossa dalla scheda ma la sua rimozione sarà tracciata nella cronologia.</p>
            <label className="block text-xs font-bold text-slate-600">Motivo {rec.completo ? '(obbligatorio, scheda completa)' : '(facoltativo)'}
              <input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} data-testid="pulw-del-reason" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setPendingDelete(null)} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
              <button onClick={confirmRemove} disabled={saving || (rec.completo && !deleteReason.trim())} data-testid="pulw-del-confirm" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-60">{saving ? 'Rimuovo…' : 'Conferma rimozione'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------- Reintegri --------------------
const ReintegriTab = ({ pool }) => {
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [fromDate, setFromDate] = useState(''); const [toDate, setToDate] = useState('');
  const load = async () => {
    const { data } = await axios.get(`${API}/piscine/${pool.id}/reintegri`, { headers: authHeaders() });
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, [pool.id]); // eslint-disable-line
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3" data-testid="piscina-reintegri-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-[#023F80]">Registro immissione, reintegro e rinnovo acqua</h3>
        <div className="flex items-center gap-2">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded" placeholder="Dal" />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded" placeholder="Al" />
          <button onClick={() => fromDate && exportPDF(pool.id, 'reintegri', fromDate, toDate || fromDate)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300 disabled:opacity-50" disabled={!fromDate}><Download size={12} /> PDF</button>
          <button onClick={() => setFormOpen(true)} data-testid="reintegro-new-btn" className="px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]">+ Nuovo intervento</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase text-slate-500">
              <th className="text-left bg-slate-100 rounded-l-lg px-3 py-2">Data</th>
              <th className="text-left bg-slate-100 px-3 py-2">Orario</th>
              <th className="text-left bg-slate-100 px-3 py-2">Causale</th>
              <th className="text-left bg-slate-100 px-3 py-2">A m³</th>
              <th className="text-left bg-slate-100 px-3 py-2">B m³</th>
              <th className="text-left bg-slate-100 px-3 py-2">B−A</th>
              <th className="text-left bg-slate-100 rounded-r-lg px-3 py-2">Esito</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`reintegro-row-${r.id}`}>
                <td className="px-3 py-2">{r.dataInizio}{r.dataFine !== r.dataInizio ? ` → ${r.dataFine}` : ''}</td>
                <td className="px-3 py-2">{r.oraInizio || '—'} → {r.oraFine || '—'}</td>
                <td className="px-3 py-2">{r.causale}{r.causaleAltro ? ` (${r.causaleAltro})` : ''}</td>
                <td className="px-3 py-2 font-mono">{r.contatoreA}</td>
                <td className="px-3 py-2 font-mono">{r.contatoreB}</td>
                <td className="px-3 py-2 font-mono font-bold">{r.mcReintegrati ?? '—'}</td>
                <td className="px-3 py-2"><EsitoBadge esito={r.esito} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="py-8 text-center text-sm text-slate-400">Nessun intervento registrato</div>}
      </div>
      {formOpen && <ReintegroForm pool={pool} onClose={() => { setFormOpen(false); load(); }} />}
    </div>
  );
};

const ReintegroForm = ({ pool, onClose }) => {
  const [f, setF] = useState({ dataInizio: todayISO(), dataFine: '', oraInizio: '', oraFine: '', causale: '', causaleAltro: '', contatoreA: '', contatoreB: '', phDopo: null, cloroLiberoDopo: null, cloroCombinatoDopo: null, note: '' });
  const [saving, setSaving] = useState(false);
  const [ncPrompt, setNcPrompt] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const mc = useMemo(() => {
    const a = parseFloat(String(f.contatoreA).replace(',', '.')), b = parseFloat(String(f.contatoreB).replace(',', '.'));
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    if (b < a) return 'ERR';
    return (b - a).toFixed(3);
  }, [f.contatoreA, f.contatoreB]);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/piscine/${pool.id}/reintegri`, f, { headers: authHeaders() });
      toast.success('Intervento salvato');
      setSavedId(data.id);
      const red = ['phDopo', 'cloroLiberoDopo', 'cloroCombinatoDopo'].filter((k) => f[k] === 'non_conforme');
      if (red.length > 0) setNcPrompt({ params: red, level: 'red' });
      else onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  const createNC = async () => {
    try {
      await axios.post(`${API}/piscine/${pool.id}/nc`, {
        registroCollegato: 'reintegro', sourceRegistroId: savedId,
        descrizione: `Reintegro con parametro NC (${ncPrompt.params.join(', ')}) del ${f.dataInizio}`,
      }, { headers: authHeaders() });
      toast.success('NC creata');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setNcPrompt(null); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reintegro-form">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto space-y-3">
        <h3 className="text-lg font-bold text-[#023F80]">Nuovo intervento reintegro</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold text-slate-600">Data inizio<input type="date" value={f.dataInizio} onChange={(e) => set('dataInizio', e.target.value)} data-testid="rein-data-inizio" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
          <label className="text-xs font-bold text-slate-600">Data fine (se attraversa mezzanotte)<input type="date" value={f.dataFine} onChange={(e) => set('dataFine', e.target.value)} data-testid="rein-data-fine" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
          <label className="text-xs font-bold text-slate-600">Ora inizio<input type="time" value={f.oraInizio} onChange={(e) => set('oraInizio', e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300" /></label>
          <label className="text-xs font-bold text-slate-600">Ora fine<input type="time" value={f.oraFine} onChange={(e) => set('oraFine', e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300" /></label>
          <label className="text-xs font-bold text-slate-600">Causale<select value={f.causale} onChange={(e) => set('causale', e.target.value)} data-testid="rein-causale" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300"><option value="">Seleziona…</option>{CAUSALI.map((c) => <option key={c}>{c}</option>)}</select></label>
          {f.causale === 'Altro' && <label className="text-xs font-bold text-slate-600">Descrizione<input value={f.causaleAltro} onChange={(e) => set('causaleAltro', e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300" /></label>}
          <label className="text-xs font-bold text-slate-600">Contatore iniziale A (m³)<input value={f.contatoreA} onChange={(e) => set('contatoreA', e.target.value)} data-testid="rein-a" placeholder="es. 100,5" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 font-mono" /></label>
          <label className="text-xs font-bold text-slate-600">Contatore finale B (m³)<input value={f.contatoreB} onChange={(e) => set('contatoreB', e.target.value)} data-testid="rein-b" placeholder="es. 103" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 font-mono" /></label>
        </div>
        <div className={`rounded-lg p-2 text-sm font-bold ${mc === 'ERR' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-800'}`} data-testid="rein-mc">m³ (B − A): {mc === 'ERR' ? '⚠️ B minore di A — correggi' : (mc ?? '—')}</div>
        <div className="grid grid-cols-3 gap-3">
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">pH dopo</div><Select value={f.phDopo} onChange={(v) => set('phDopo', v)} options={PH_OPTS} testId="rein-ph" /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro libero</div><Select value={f.cloroLiberoDopo} onChange={(v) => set('cloroLiberoDopo', v)} options={CLORO_LIB_OPTS} testId="rein-cl" /></div>
          <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro combinato</div><Select value={f.cloroCombinatoDopo} onChange={(v) => set('cloroCombinatoDopo', v)} options={CLORO_COMB_OPTS} testId="rein-cc" /></div>
        </div>
        <label className="block text-xs font-bold text-slate-600">Note<textarea value={f.note} onChange={(e) => set('note', e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={saving || mc === 'ERR'} data-testid="rein-save" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-60">{saving ? 'Salvo…' : 'Salva intervento'}</button>
        </div>
      </div>
      <NCAlertPopup open={!!ncPrompt} params={ncPrompt?.params || []} level={ncPrompt?.level} onCreate={createNC} onClose={() => { setNcPrompt(null); onClose(); }} />
    </div>
  );
};

// -------------------- NC --------------------
const NCTab = ({ pool }) => {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [fromDate, setFromDate] = useState(''); const [toDate, setToDate] = useState('');
  const load = async () => {
    const { data } = await axios.get(`${API}/piscine/${pool.id}/nc`, { headers: authHeaders() });
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, [pool.id]); // eslint-disable-line
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3" data-testid="piscina-nc-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-[#023F80]">Registro non conformità</h3>
        <div className="flex items-center gap-2">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded" />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded" />
          <button onClick={() => fromDate && exportPDF(pool.id, 'nc', fromDate, toDate || fromDate)} disabled={!fromDate} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300 disabled:opacity-50"><Download size={12} /> PDF</button>
          <button onClick={() => setNewOpen(true)} data-testid="nc-new-btn" className="px-4 py-2 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-700">+ Nuova NC</button>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((n) => (
          <div key={n.id} className={`rounded-lg border p-3 cursor-pointer hover:bg-slate-50 ${n.statoPratica === 'risolta' ? 'border-slate-200 opacity-70' : 'border-red-200 bg-red-50/40'}`} onClick={() => setOpenId(n.id)} data-testid={`nc-item-${n.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-bold text-slate-800">{n.descrizione || '(descrizione da compilare)'}</div>
                <div className="text-[11px] text-slate-500">{n.dataOra?.slice(0, 16)} · Registro origine: <b>{n.registroCollegato || '—'}</b> · Stato: <b>{n.statoPratica}</b></div>
              </div>
              <EsitoBadge esito={n.esitoRiapertura} />
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="py-8 text-center text-sm text-slate-400">Nessuna non conformità registrata</div>}
      </div>
      {newOpen && <NCForm pool={pool} onClose={() => { setNewOpen(false); load(); }} />}
      {openId && <NCEdit pool={pool} ncId={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
};

const NCForm = ({ pool, onClose }) => {
  const [desc, setDesc] = useState(''); const [reg, setReg] = useState('altro'); const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/piscine/${pool.id}/nc`, { descrizione: desc, registroCollegato: reg, dataOra: new Date().toISOString() }, { headers: authHeaders() });
      toast.success('NC creata'); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nc-new-form">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-3">
        <h3 className="text-lg font-bold text-red-800">Nuova non conformità</h3>
        <label className="block text-xs font-bold text-slate-600">Registro collegato
          <select value={reg} onChange={(e) => setReg(e.target.value)} data-testid="nc-registro" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm">
            <option value="acqua">Acqua</option><option value="pulizie">Pulizie</option><option value="reintegro">Reintegro</option><option value="altro">Altro</option>
          </select>
        </label>
        <label className="block text-xs font-bold text-slate-600">Descrizione<textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="nc-desc" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="nc-save" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold">Salva</button>
        </div>
      </div>
    </div>
  );
};

const NCEdit = ({ pool, ncId, onClose }) => {
  const [nc, setNc] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/piscine/${pool.id}/nc`, { headers: authHeaders() }).then((r) => setNc((r.data.items || []).find((x) => x.id === ncId) || null));
  }, [pool.id, ncId]);
  const set = (k, v) => setNc((x) => ({ ...x, [k]: v }));
  const toggleAz = (a) => setNc((x) => ({ ...x, azioniImmediate: (x.azioniImmediate || []).includes(a) ? (x.azioniImmediate || []).filter((z) => z !== a) : [...(x.azioniImmediate || []), a] }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/piscine/${pool.id}/nc/${ncId}`, nc, { headers: authHeaders() });
      setNc(data); toast.success('NC aggiornata');
      if (data.statoPratica === 'risolta') onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };
  if (!nc) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nc-edit">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-red-800">Non conformità · {nc.statoPratica}</h3><EsitoBadge esito={nc.esitoRiapertura} /></div>
        <label className="block text-xs font-bold text-slate-600">Descrizione<textarea value={nc.descrizione || ''} onChange={(e) => set('descrizione', e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <div>
          <div className="text-xs font-bold text-slate-600 mb-1">Azione immediata (multipla)</div>
          <div className="flex flex-wrap gap-2">
            {['Accesso sospeso', 'Avviso utenti', 'Altro'].map((a) => (
              <button key={a} onClick={() => toggleAz(a)} data-testid={`nc-az-${a}`} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${(nc.azioniImmediate || []).includes(a) ? 'bg-red-600 text-white border-red-700' : 'bg-white border-slate-300 text-slate-700'}`}>{a}</button>
            ))}
          </div>
          {(nc.azioniImmediate || []).includes('Altro') && <input value={nc.azioneImmediataAltro || ''} onChange={(e) => set('azioneImmediataAltro', e.target.value)} placeholder="Descrivi…" className="w-full mt-2 px-2 py-1.5 rounded border border-slate-300 text-sm" />}
        </div>
        <label className="block text-xs font-bold text-slate-600">Azione correttiva<textarea value={nc.azioneCorrettiva || ''} onChange={(e) => set('azioneCorrettiva', e.target.value)} rows={2} data-testid="nc-azione-corr" placeholder="Descrivere l'intervento effettuato, ad esempio clorazione shock, pulizia pareti, controlavaggio." className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-3">
          <div className="flex items-center justify-between"><div className="text-xs font-bold text-slate-700">Verifica efficacia</div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!nc.verifChimNonApplicabile} onChange={(e) => set('verifChimNonApplicabile', e.target.checked)} data-testid="nc-na" /> Verifica chimica non applicabile</label>
          </div>
          {nc.verifChimNonApplicabile ? (
            <input value={nc.verifChimMotivazione || ''} onChange={(e) => set('verifChimMotivazione', e.target.value)} placeholder="Motivazione + descrizione verifica alternativa…" className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">pH</div><Select value={nc.verificaPh} onChange={(v) => set('verificaPh', v)} options={PH_OPTS} testId="nc-ver-ph" /></div>
              <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro libero</div><Select value={nc.verificaCloroLibero} onChange={(v) => set('verificaCloroLibero', v)} options={CLORO_LIB_OPTS} testId="nc-ver-cl" /></div>
              <div><div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cloro combinato</div><Select value={nc.verificaCloroCombinato} onChange={(v) => set('verificaCloroCombinato', v)} options={CLORO_COMB_OPTS} testId="nc-ver-cc" /></div>
            </div>
          )}
          <div className="text-[11px] text-slate-500 italic">Un esito favorevole della verifica non riapre automaticamente la piscina né risolve altre anomalie della stessa vasca (es. torbidità, solidi, pulizie).</div>
        </div>
        <div className="flex justify-between gap-2 flex-wrap">
          <select value={nc.statoPratica} onChange={(e) => set('statoPratica', e.target.value)} data-testid="nc-stato-select" className="px-3 py-1.5 rounded border border-slate-300 text-sm">
            <option value="aperta">Aperta</option><option value="in_gestione">In gestione</option><option value="risolta">Risolta</option>
          </select>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Chiudi</button>
            <button onClick={save} disabled={saving} data-testid="nc-save-edit" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold">{saving ? 'Salvo…' : 'Salva'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------- Calendario --------------------
const CalendarTab = ({ pool, onNav }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/piscine/${pool.id}/calendar`, { params: { year, month }, headers: authHeaders() });
      setDays(data.days || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pool.id, year, month]); // eslint-disable-line
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to = days.length > 0 ? days[days.length - 1].date : from;
  const statoIcon = (s) => ({ aperta: '🟢', chiusa: '🔴', non_dichiarato: '⚪' }[s] || '⚪');
  const acquaColor = (esito) => ({ non_conforme: 'bg-red-500', attenzione: 'bg-amber-400', conforme: 'bg-emerald-500', incompleto: 'bg-slate-300' }[esito] || 'bg-slate-200');
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3" data-testid="piscina-calendar-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><CalIcon size={16} className="text-[#023F80]" /><h3 className="font-bold text-[#023F80]">Calendario mensile</h3></div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="cal-month" className="px-2 py-1 text-sm border border-slate-300 rounded">
            {['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="cal-year" className="px-2 py-1 text-sm border border-slate-300 rounded">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => exportPDF(pool.id, 'acqua', from, to)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300"><Download size={12} /> PDF Acqua</button>
          <button onClick={() => exportPDF(pool.id, 'pulizie_daily', from, to)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-xs font-bold hover:bg-slate-300"><Download size={12} /> PDF Pulizie</button>
          <button onClick={() => exportPDF(pool.id, 'all', from, to)} data-testid="cal-pdf-all" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]"><Download size={12} /> PDF Tutti i registri</button>
        </div>
      </div>
      {loading && <Loader2 className="animate-spin" />}
      {!loading && (
        <div className="grid grid-cols-7 gap-1 text-[11px]" data-testid="cal-grid">
          {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => <div key={i} className="p-1 font-bold text-center text-slate-500">{d}</div>)}
          {days.length > 0 && Array.from({ length: (new Date(days[0].date).getDay() || 7) - 1 }, (_, i) => <div key={`e${i}`} />)}
          {days.map((d) => (
            <button key={d.date} onClick={() => onNav && onNav('acqua', d.date)} className={`text-left p-1 border rounded hover:ring-2 hover:ring-[#023F80] transition ${d.stato === 'chiusa' ? 'bg-red-50 border-red-200' : d.stato === 'aperta' ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`cal-day-${d.date}`} title={`${d.date} · ${d.stato}${d.statoMotivo ? ` (${d.statoMotivo})` : ''} — clicca per aprire il controllo acqua`}>
              <div className="flex justify-between"><span className="font-bold text-slate-700">{parseInt(d.date.slice(-2), 10)}</span><span>{statoIcon(d.stato)}</span></div>
              <div className="flex gap-0.5 mt-1">
                <div className={`h-1.5 flex-1 rounded ${acquaColor(d.acqua.overall)}`} title={`Acqua: ${d.acqua.overall}`} />
                <div className={`h-1.5 flex-1 rounded ${d.pulizieCompleto ? 'bg-emerald-500' : d.pulizieParziale ? 'bg-amber-300' : 'bg-slate-200'}`} title="Pulizie" />
              </div>
              {d.ncAperte > 0 && <div className="mt-1 text-[9px] text-red-600 font-bold">! {d.ncAperte} NC</div>}
              {d.reintegri > 0 && <div className="text-[9px] text-slate-500">+ {d.reintegri} reint.</div>}
            </button>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-500 space-x-3">
        <span>🟢 Aperta</span><span>🔴 Chiusa</span><span>⚪ Non dichiarato</span>
        <span>· Barra 1 = acqua, Barra 2 = pulizie</span>
      </div>
    </div>
  );
};

// -------------------- Delete Pool --------------------
const DeletePoolDialog = ({ pool, companyName, onClose, onDeleted }) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = reason.trim().length >= 3 && confirmText.trim().toUpperCase() === 'ELIMINA' && !busy;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/piscine/${pool.id}`, {
        params: { reason: reason.trim() },
        headers: authHeaders(),
      });
      if (data.mode === 'soft') {
        const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
        toast.success(`Vasca disattivata (soft-delete). ${total} registrazioni mantenute nello storico.`);
      } else {
        toast.success('Vasca eliminata definitivamente (nessuna registrazione presente).');
      }
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione vasca');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-pool-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={busy ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 border-t-4 border-red-600 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={28} className="text-red-600 shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-red-800">Elimina vasca</h3>
            <p className="text-sm text-slate-700 mt-1">
              Stai per eliminare la vasca <b className="text-[#023F80]">{pool.name}</b>
              {companyName && <> dell'azienda <b className="text-[#023F80]">{companyName}</b></>}.
            </p>
            <ul className="text-xs text-slate-600 mt-2 list-disc pl-4 space-y-0.5">
              <li>Se la vasca <b>non ha registrazioni</b>, viene eliminata definitivamente.</li>
              <li>Se contiene registrazioni (controllo acqua, pulizie, reintegri, NC…), la vasca viene <b>rimossa dall'elenco operativo</b> ma lo storico resta accessibile per audit (soft-delete).</li>
              <li>L'operazione viene registrata nell'<b>audit log</b> con utente, data e motivazione.</li>
            </ul>
          </div>
        </div>
        <label className="block text-xs font-bold text-slate-600">Motivazione (obbligatoria, min 3 caratteri)
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="delete-pool-reason" placeholder="Es. Vasca dismessa / rifacimento impianto / errore di configurazione" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
        </label>
        <label className="block text-xs font-bold text-slate-600">Conferma digitando <code className="px-1 py-0.5 bg-slate-100 rounded">ELIMINA</code>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-pool-confirm-input" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={!canSubmit} data-testid="delete-pool-confirm-btn" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-50">{busy ? 'Elimino…' : 'Conferma eliminazione'}</button>
        </div>
      </div>
    </div>
  );
};

// -------------------- New Pool --------------------
const NewPoolDialog = ({ companies, onClose, onCreated }) => {
  const [cid, setCid] = useState(''); const [name, setName] = useState(''); const [note, setNote] = useState(''); const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/piscine/enable/${cid}?enabled=true`, {}, { headers: authHeaders() }).catch(() => {});
      await axios.post(`${API}/piscine`, { companyId: cid, name, note }, { headers: authHeaders() });
      toast.success('Piscina creata. Modulo abilitato per l\'azienda.');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-pool-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#023F80]">Nuova piscina</h3>
        <label className="block text-xs font-bold text-slate-600">Azienda cliente
          <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="pool-company" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm">
            <option value="">Seleziona…</option>
            {companies.filter((c) => c.id && c.id !== 'privati').map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block text-xs font-bold text-slate-600">Nome piscina<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Vasca principale, Vasca bambini…" data-testid="pool-name" className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <label className="block text-xs font-bold text-slate-600">Note<input value={note} onChange={(e) => setNote(e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={!cid || !name || saving} data-testid="pool-save" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-60">{saving ? 'Salvo…' : 'Crea piscina'}</button>
        </div>
      </div>
    </div>
  );
};

export default PiscinaPage;
